import React, { useCallback, useEffect, useState } from "react";

import { Skeleton, Typography } from "@packages/ds";
import { FormattedMessage, useIntl } from "react-intl";

import { removeContinuousProfiling } from "Api/observability/continuousProfiling/removeContinuousProfiling.request";
import {
  getAddonsRequest,
  SubscriptionAddon
} from "Api/organizations/subscription/getSubscriptionAddons.request";
import FormattedMessageWithConfigValues from "Components/FormattedMessageWithConfigValues";
import { Button, ButtonWrapper } from "ds/Button";
import Modal from "ds/Modal";
import PageLoading from "ds/PageLoading";
import { WizardStepperWrapper } from "ds/WizardStepper";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import {
  discountsErrorsSelector,
  discountsLoadingSelector,
  discountsSelector
} from "Reducers/discount";
import { getDiscounts } from "Reducers/discount/thunks/getDiscounts.thunk";
import { integrationsSelector } from "Reducers/integration";
import { addonsCurrentSelector, getAddons } from "Reducers/organization/addons";
import { useOrganizationProfile } from "Reducers/organization/hooks/useOrganizationProfile";
import { useLoadMembers } from "Reducers/organization/members/hooks/useLoadMembers";
import {
  selectFilteredMembers,
  selectIsLoadingMembers
} from "Reducers/organization/members/membersList.selectors";
import {
  loadSubscriptions,
  selectSubscriptionDetails
} from "Reducers/organization/subscription";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";
import { UserManagementAddonModal } from "src/organization/pages/billing/organizationBilling/components/AddonModals";
import ProjectBreakdown from "src/organization/pages/billing/projectBilling/components/ProjectBreakdown/ProjectBreakdown";
import useLoadIntegrations from "src/organization/pages/project/settings/integrations/useLoadIntegrations";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import { Content, ContentScrollable } from "../Content";
import { Footer } from "../Footer";

import * as D from "./DetailsTable";
import * as S from "./Review.styles";
import { useOrganizationEstimate } from "./useOrganizationEstimate";
import { useTrialprojects } from "./useTrialProjects";

import type {
  Organization,
  SubscriptionEstimateComplex
} from "@packages/client";

type Props = {
  organization: Organization;
};

const ContinuousProfilingRemoveModal = ({
  isOpen,
  onOpenChange,
  organizationId,
  subscriptionId
}) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    await removeContinuousProfiling(organizationId, subscriptionId);
    setIsLoading(false);
    onOpenChange(false);
  };

  return (
    <Modal
      size="medium"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      handleClose={() => onOpenChange(false)}
    >
      <Typography.Title tag="h3">
        {intl.formatMessage({ id: "continuous_profiling_removal_modal.title" })}
      </Typography.Title>
      <Typography.BodyText>
        {intl.formatMessage({
          id: "continuous_profiling_removal_modal.description"
        })}
      </Typography.BodyText>
      <ButtonWrapper justifyContent="end" spacing="default">
        <Button
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={isLoading}
        >
          {intl.formatMessage({
            id: "continuous_profiling_removal_modal.cancel"
          })}
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {intl.formatMessage({
            id: "continuous_profiling_removal_modal.disable"
          })}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};

export const Review = ({ organization }: Props) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const trial = useTrialprojects(organization.name);
  const estimate = useOrganizationEstimate(organization);
  const loadMembers = useLoadMembers(organization.id);
  const members = useAppSelector(selectFilteredMembers);
  const membersLoading = useAppSelector(selectIsLoadingMembers);
  const allowanceDiscounts = useAppSelector(discountsSelector);
  const allowanceDiscountError = useAppSelector(discountsErrorsSelector);
  const subscriptions = useAppSelector(
    state => state.organizationSubscription.data
  );
  const bulkSubscription = Object.values(
    Object.values(subscriptions)[0] ?? {}
  )[0];
  const subscription = useAppSelector(state =>
    selectSubscriptionDetails(state, {
      organizationId: organization?.name,
      subscriptionId: bulkSubscription?.id
    })
  );
  const projectId = subscription?.project_id ?? "";
  const isIntegrationsLoading = useLoadIntegrations({
    defaultProjectId: projectId
  });
  const integrations = useAppSelector(state =>
    integrationsSelector(state, { projectId: projectId })
  );
  const logForwardingIntegrationsTypes = [
    "syslog",
    "splunk",
    "sumologic",
    "newrelic",
    "httplog"
  ];
  const logForwardingIntegration = integrations.find(integration =>
    logForwardingIntegrationsTypes.includes(integration.type)
  );
  const [subscriptionAddon, setSubscriptionAddon] =
    useState<SubscriptionAddon>();
  const [isContinuousProfilingModalOpen, setIsContinuousProfilingModalOpen] =
    useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState<
    null | boolean
  >(null);
  const [subscriptionFutureLoading, setSubscriptionFutureLoading] = useState<
    null | boolean
  >(null);
  const [subscriptionAddonLoading, setSubscriptionAddonLoading] = useState<
    null | boolean
  >(null);
  const [estimateFuture, setEstimateFuture] =
    useState<SubscriptionEstimateComplex>();
  const { organizationProfile } = useOrganizationProfile(organization.name);
  const currentAddons = useSelectorWithUrlParams(addonsCurrentSelector);
  const isDiscountLoading = useAppSelector(discountsLoadingSelector);
  const getFuture = useCallback(async () => {
    setSubscriptionFutureLoading(true);
    const future = await subscription?.getEstimate({
      format: "complex",
      include_components: true
    });

    setEstimateFuture(future);
    setSubscriptionFutureLoading(false);
  }, [subscription]);

  const getSubscriptionAddon = useCallback(async () => {
    setSubscriptionAddonLoading(true);
    const addon = await getAddonsRequest(
      organization.id,
      subscription?.id ?? ""
    );
    setSubscriptionAddon(addon);
    setSubscriptionAddonLoading(false);
  }, [organization.id, subscription?.id]);

  const getSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    const subscriptions = await dispatch(
      loadSubscriptions({ organizationId: organization.name })
    );
    const subscription = Object.values(
      Object.values(subscriptions.payload.items)[0] ?? {}
    )[0];
    setSubscriptionLoading(false);
    return subscription;
  }, [dispatch, organization.name]);

  useEffect(() => {
    if (!subscription && subscriptionLoading === null) getSubscription();
  }, [getSubscription, subscription, subscriptionLoading]);

  useEffect(() => {
    if (subscription && !subscriptionAddon && subscriptionAddonLoading === null)
      getSubscriptionAddon();
  }, [
    getSubscriptionAddon,
    subscription,
    subscriptionAddon,
    subscriptionAddonLoading
  ]);

  useEffect(() => {
    if (subscription && !estimateFuture && subscriptionFutureLoading === null)
      getFuture();
  }, [estimateFuture, getFuture, subscription, subscriptionFutureLoading]);

  useEffect(() => {
    if (!allowanceDiscounts && !allowanceDiscountError && !isDiscountLoading)
      dispatch(getDiscounts());
  }, [allowanceDiscounts, allowanceDiscountError, dispatch, isDiscountLoading]);

  useEffect(() => {
    if (!members.length && !membersLoading) loadMembers();
  }, [loadMembers, members.length, membersLoading]);

  useEffect(() => {
    if (organization && !currentAddons)
      dispatch(getAddons({ organizationId: organization.name }));
  }, [currentAddons, dispatch, organization]);

  const currency = organizationProfile?.currency;

  const allowanceDiscountForOrg =
    currency && allowanceDiscounts?.currencies[currency].formatted;

  const shouldShowProjectsSection =
    estimate.data?.subscriptions?.list &&
    estimate.data?.subscriptions?.list.length > 0;

  const project = estimate.data?.subscriptions.list[0];

  if (
    estimate.isLoading ||
    isIntegrationsLoading ||
    subscriptionLoading ||
    membersLoading
  ) {
    return (
      <S.LoaderWrapper>
        <PageLoading />
      </S.LoaderWrapper>
    );
  }

  return (
    <>
      <Content>
        <ContentScrollable>
          <S.ColumnCenter>
            <WizardStepperWrapper
              hasLabel
              steps={[
                {
                  number: 1,
                  status: "active",
                  label: <FormattedMessage id="subscribe.stepper.review" />,
                  stepPath: `/${organization.name}/-/subscribe`
                },
                {
                  number: 2,
                  status: "default",
                  label: (
                    <FormattedMessage id="subscribe.stepper.billingDetails" />
                  )
                }
              ]}
            />
            <Typography.Title tag="h1">
              <FormattedMessageWithConfigValues id="subscribe.review.title" />
            </Typography.Title>
            <Typography.BodyText>
              {trial.isLoading ? (
                <Skeleton>
                  <Skeleton.Square
                    height="32px"
                    fixedWidth
                    width="300px"
                    margin="0 0 32px 0"
                  />
                </Skeleton>
              ) : trial.isActive ? (
                <FormattedMessageWithConfigValues id="subscribe.review.descriptionActive" />
              ) : (
                <FormattedMessageWithConfigValues id="subscribe.review.descriptionExpired" />
              )}
            </Typography.BodyText>
          </S.ColumnCenter>
          <S.BorderedCard padding="32px 24px">
            <div>
              <Typography.BodyText size="extraSmall">
                <FormattedMessage id="subscribe.review.organization" />
              </Typography.BodyText>
              <Typography.Title tag="h3">{organization.label}</Typography.Title>
            </div>
            <S.Totals>
              <S.Row>
                <S.Column>
                  {estimate.isLoading ? (
                    <Skeleton synchronize>
                      <Skeleton.Square height="24px" />
                    </Skeleton>
                  ) : (
                    <Typography.Title tag="h3">
                      {estimate.data?.subscriptions.list.length}
                    </Typography.Title>
                  )}
                  <Typography.Label $color="neutral-600">
                    <FormattedMessage id="subscribe.review.projects" />
                  </Typography.Label>
                </S.Column>
                <S.Column>
                  {estimate.isLoading ? (
                    <Skeleton synchronize>
                      <Skeleton.Square height="24px" />
                    </Skeleton>
                  ) : (
                    <Typography.Title tag="h3">
                      {estimate.data?.user_licenses.base.count || 0}
                    </Typography.Title>
                  )}
                  <Typography.Label $color="neutral-600">
                    <FormattedMessage id="subscribe.review.users" />
                  </Typography.Label>
                </S.Column>
                <S.Column>
                  {estimate.isLoading ? (
                    <Skeleton synchronize>
                      <Skeleton.Square height="24px" />
                    </Skeleton>
                  ) : (
                    <Typography.Title tag="h3">
                      {estimate.data?.support_level || 0}
                    </Typography.Title>
                  )}
                  <Typography.Label $color="neutral-600">
                    <FormattedMessage id="subscribe.review.support" />
                  </Typography.Label>
                </S.Column>
              </S.Row>
              <S.Total>
                {allowanceDiscountForOrg && (
                  <S.FirstProjectIncentiveTag variant="rose">
                    {intl.formatMessage(
                      {
                        id: "first_project_incentive.tag.indicator"
                      },
                      { allowanceDiscount: allowanceDiscountForOrg }
                    )}
                  </S.FirstProjectIncentiveTag>
                )}
                {estimate.isLoading ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="24px" fixedWidth width="100px" />
                  </Skeleton>
                ) : (
                  <>
                    {" "}
                    <S.TotalQuantityDiscount as="span" tag="h2">
                      {estimate.data?.total}
                    </S.TotalQuantityDiscount>
                    <S.TotalQuantity as="span" tag="h2">
                      {estimate.data?.sub_total}
                    </S.TotalQuantity>
                  </>
                )}
                <Typography.Label $color="neutral-600">
                  <FormattedMessage id="subscribe.review.monthly" />
                </Typography.Label>
              </S.Total>
            </S.Totals>
          </S.BorderedCard>
          {shouldShowProjectsSection && (
            <D.Layout>
              <D.Header>
                <Typography.Title tag="h3">
                  <FormattedMessage id="subscribe.review.projects" />
                </Typography.Title>
                <Typography.Label $color="neutral-600">
                  <FormattedMessage id="subscribe.review.monthlyEstimate" />
                </Typography.Label>
              </D.Header>
              {estimate.isLoading && (
                <Skeleton synchronize>
                  <Skeleton.Square height="32px" margin="8px 0 8px 0" />
                  <Skeleton.Square height="32px" margin="0 0 8px 0" />
                  <Skeleton.Square height="32px" margin="0 0 8px 0" />
                </Skeleton>
              )}
              {project && subscription && estimateFuture && (
                <div>
                  <D.Row key={project.project_title} $shouldForceLessSpacing>
                    <S.Row $gap="8px">
                      <S.UserLabel tag="h5">
                        {project.project_title}
                      </S.UserLabel>
                    </S.Row>
                    <Typography.BodyText weight="bold">
                      {project.total}
                    </Typography.BodyText>
                  </D.Row>
                  <ProjectBreakdown
                    isTrial
                    subscription={subscription}
                    estimateFuture={estimateFuture}
                    continuousProfilingCost={
                      estimateFuture.continuous_profiling
                    }
                    onContinuousProfilingRemoveClick={() =>
                      setIsContinuousProfilingModalOpen(true)
                    }
                    canDowngradeContinuousProfiling
                  />
                </div>
              )}
            </D.Layout>
          )}
          <D.Layout>
            <D.GridHeader>
              <Typography.Title tag="h3">
                <FormattedMessage id="subscribe.review.users" />
              </Typography.Title>
              <Typography.Label>
                <FormattedMessage id="subscribe.review.unitPrice" />
              </Typography.Label>
              <Typography.Label>
                <FormattedMessage id="subscribe.review.quantity" />
              </Typography.Label>
              <Typography.Label $color="neutral-600">
                <FormattedMessage id="subscribe.review.monthlyEstimate" />
              </Typography.Label>
            </D.GridHeader>
            <D.GridRow>
              <S.Row $gap="8px">
                <S.UserLabel tag="h5">
                  <FormattedMessage id="subscribe.review.userLicenses" />
                </S.UserLabel>
                {estimate.isLoading ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="24px" fixedWidth width="100px" />
                  </Skeleton>
                ) : (
                  <Button
                    variant="link"
                    to={getUrl({
                      key: "organization.settings.users",
                      props: { organizationId: organization.name }
                    })}
                  >
                    <FormattedMessage id="subscribe.review.userLicenses.manage" />
                  </Button>
                )}
              </S.Row>
              <div>{estimate.data?.options.standard_management_user}</div>
              <div>
                {estimate.data?.user_licenses.base.list.admin_user.count}
              </div>
              <div>
                {estimate.isLoading ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="24px" fixedWidth width="100px" />
                  </Skeleton>
                ) : (
                  <Typography.BodyText $color="ebony" weight="bold">
                    {estimate.data?.user_licenses.base.list.admin_user.total}
                  </Typography.BodyText>
                )}
              </div>
            </D.GridRow>
            <S.BadgeSection>
              {members.map(member => (
                <S.Badge key={member.member.id}>
                  {member.user?.username}
                </S.Badge>
              ))}
            </S.BadgeSection>
            <O.Separator type="minor" margin="0 24px" />
            <D.Row>
              <S.Row $gap="16px">
                <S.UserLabel tag="h5">
                  <FormattedMessage id="subscribe.review.userViewers" />
                </S.UserLabel>
                {estimate.isLoading ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="24px" fixedWidth width="100px" />
                  </Skeleton>
                ) : (
                  <Typography.BodyText>
                    <FormattedMessage
                      id="subscribe.review.usersWithCount"
                      values={{
                        count:
                          estimate.data?.user_licenses.base.list.viewer_user
                            .count
                      }}
                    />
                  </Typography.BodyText>
                )}
              </S.Row>
              {estimate.isLoading ? (
                <Skeleton synchronize>
                  <Skeleton.Square height="24px" fixedWidth width="100px" />
                </Skeleton>
              ) : (
                <Typography.BodyText $color="ebony" weight="bold">
                  <FormattedMessage id="subscribe.review.free" />
                </Typography.BodyText>
              )}
            </D.Row>
          </D.Layout>
          {currentAddons?.user_management && (
            <D.Layout>
              <D.Header>
                <Typography.Title tag="h3">
                  <FormattedMessage id="subscribe.review.organization_addons" />
                </Typography.Title>
                <Typography.Label $color="neutral-600">
                  <FormattedMessage id="subscribe.review.monthlyEstimate" />
                </Typography.Label>
              </D.Header>
              <D.Row>
                <S.Row $gap="16px">
                  <S.UserLabel tag="h5">
                    <FormattedMessage id="subscribe.review.organization_addons.user_management" />
                  </S.UserLabel>
                  <Button
                    variant="link"
                    onClick={() => setShowUpgradeModal(true)}
                  >
                    <FormattedMessage id="subscribe.review.organization_addons.remove" />
                  </Button>
                </S.Row>
                {estimate.isLoading ? (
                  <Skeleton synchronize>
                    <Skeleton.Square height="24px" fixedWidth width="100px" />
                  </Skeleton>
                ) : (
                  <Typography.BodyText>
                    {estimate.data?.user_licenses.user_management.total}
                  </Typography.BodyText>
                )}
              </D.Row>
            </D.Layout>
          )}
          {!!logForwardingIntegration && (
            <D.Layout>
              <D.GridHeader>
                <Typography.Title tag="h3">
                  <FormattedMessage id="subscribe.review.integrations" />
                </Typography.Title>
                <Typography.Label>
                  <FormattedMessage id="subscribe.review.unitPrice" />
                </Typography.Label>
                <Typography.Label>
                  <FormattedMessage id="subscribe.review.quantity" />
                </Typography.Label>
                <Typography.Label $color="neutral-600">
                  <FormattedMessage id="subscribe.review.monthlyEstimate" />
                </Typography.Label>
              </D.GridHeader>
              <D.GridRow>
                <S.Row $gap="16px">
                  <S.UserLabel tag="h5">
                    <FormattedMessage id="subscribe.review.integrations.log_forwarding" />
                  </S.UserLabel>
                  <Button
                    variant="link"
                    to={getUrl({
                      key: "organization.project.settings.integrations.integration.configure",
                      props: {
                        organizationId: organization.name,
                        projectId: projectId,
                        integrationId: logForwardingIntegration.id
                      }
                    })}
                  >
                    <FormattedMessage id="subscribe.review.integrations.remove" />
                  </Button>
                </S.Row>
                <div> {estimateFuture?.logs_fwd_content_size.formatted}</div>
                <div> {estimateFuture?.logs_fwd_content_size.quantity}</div>
                <div>
                  {estimate.isLoading ? (
                    <Skeleton synchronize>
                      <Skeleton.Square height="24px" fixedWidth width="100px" />
                    </Skeleton>
                  ) : (
                    <Typography.BodyText>
                      {estimateFuture?.logs_fwd_content_size.formatted}
                    </Typography.BodyText>
                  )}
                </div>
              </D.GridRow>
            </D.Layout>
          )}
          <S.Notices>
            <S.TermsInfo size="extraSmall" weight="semiBold">
              <FormattedMessage id="subscribe.review.terms.note_1" />
            </S.TermsInfo>{" "}
            <S.TermsInfo size="extraSmall" weight="semiBold">
              <FormattedMessage id="subscribe.review.terms.note_2" />
            </S.TermsInfo>{" "}
            <S.TermsInfo size="extraSmall" weight="semiBold">
              <FormattedMessage
                id="subscribe.review.terms.note_3"
                values={{
                  link: text => (
                    <S.PricingButton
                      variant="link"
                      href={intl.formatMessage({
                        id: "links.pricing"
                      })}
                      analyticId="links.pricing"
                    >
                      {text}
                    </S.PricingButton>
                  )
                }}
              />
            </S.TermsInfo>
          </S.Notices>
        </ContentScrollable>
      </Content>
      <Footer>
        <Button
          variant="secondary"
          to=".."
          analyticId="subscribe.review.cancel"
        >
          <FormattedMessage id="subscribe.review.cancel" />
        </Button>
        <Button
          variant="primary"
          to="billing-details"
          analyticId="subscribe.review.addBillingDetails"
        >
          <FormattedMessage id="subscribe.review.addBillingDetails" />
        </Button>
      </Footer>
      <ContinuousProfilingRemoveModal
        isOpen={isContinuousProfilingModalOpen}
        onOpenChange={setIsContinuousProfilingModalOpen}
        organizationId={organization.id}
        subscriptionId={subscription?.id}
      />
      <UserManagementAddonModal
        isOpen={showUpgradeModal}
        setIsOpen={setShowUpgradeModal}
        onUpgrade={estimate.load}
      />
    </>
  );
};
